/**
 * Get translated label for a measurement field
 * @param fieldKey - The measurement field key (e.g., 'chest', 'waist', 'shoulder_width')
 * @param t - Translation function from useLanguage hook
 * @returns Translated label or formatted field key if translation not found
 */
export const getMeasurementLabel = (fieldKey: string, t: (key: string) => string): string => {
  // Normalize field key (handle variations like shoulder_width vs shoulderWidth)
  const normalizedKey = fieldKey.toLowerCase().replace(/([A-Z])/g, '_$1').replace(/^_/, '');
  
  // Try to get translation
  const translationKey = `measurement.${normalizedKey}`;
  const translated = t(translationKey);
  
  // If translation exists and is not the key itself, return it
  if (translated && translated !== translationKey) {
    return translated;
  }
  
  // Fallback: format the key nicely
  return fieldKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Get all measurement field labels as a map
 * Useful for bulk operations
 */
export const getMeasurementLabels = (t: (key: string) => string): Record<string, string> => {
  const fields = [
    // Upper Body
    'neck', 'shoulder', 'shoulder_width', 'chest', 'upper_waist', 'upper_hip', 'waist', 
    'bicep', 'forearm', 'wrist', 'sleeve_length', 'sleeve_width',
    'armhole', 'upper_length', 'front_length', 'center_back', 'back_length',
    // Lower Body
    'hips', 'thigh', 'knee', 'calf', 'ankle', 
    'inseam', 'outseam', 'crotch', 'pant_length', 'bottom',
    // Additional
    'height', 'weight',
    // Legacy/Alternative names
    'bust', 'under_bust', 'arm_length', 'elbow', 'hem', 'cuff', 'pet', 'front', 'back'
  ];
  
  const labels: Record<string, string> = {};
  fields.forEach(field => {
    labels[field] = getMeasurementLabel(field, t);
  });
  
  return labels;
};

